import {useState, useEffect, useRef, useCallback} from 'react';
import {SocialMediaPost, SocialStreamTab} from '@/types/socialMediaPost';
import {COLUMN_IDS, type ColumnId} from '../constants';

type ColumnVisibilityEvaluator = (posts: SocialMediaPost[]) => boolean;

const TIKTOK_NETWORK = 'tiktok_v2';

type ColumnAvailabilityCheck = (
  network?: string | null,
  socialStream?: SocialStreamTab
) => boolean;

/** Per-column availability rules. Columns not listed here are always available. */
const COLUMN_AVAILABILITY: Partial<Record<ColumnId, ColumnAvailabilityCheck>> =
  {
    [COLUMN_IDS.avgWatchTime]: (network, socialStream) => {
      if (network === TIKTOK_NETWORK) return true;
      return socialStream === 'reels' || socialStream === 'all';
    },
    // social-data aliases TikTok `impression_count` to `view_count`, so an
    // Impressions column would just duplicate Views. Hide it for TikTok.
    [COLUMN_IDS.impressions]: (network) => network !== TIKTOK_NETWORK,
    // Organic/paid saves only exist for TikTok TTO report data.
    [COLUMN_IDS.favoritesOrganic]: (network) => network === TIKTOK_NETWORK,
    [COLUMN_IDS.favoritesPaid]: (network) => network === TIKTOK_NETWORK,
  };

/** Returns whether a column is available for the given network + stream combination */
export const isColumnAvailable = (
  columnId: ColumnId,
  network?: string | null,
  socialStream?: SocialStreamTab
): boolean => COLUMN_AVAILABILITY[columnId]?.(network, socialStream) ?? true;

const getEngagementAccessorByNetwork = (network?: string | null) =>
  (
    ({
      tiktok_v2: 'engagementRateOrganic',
    }) as const
  )[network ?? ''] ?? 'engagementRate';

/**
 * Per-stream column sets shown when the posts list is empty.
 *
 * The "data-aware" defaults (computed by `columnVisibilityEvaluators` below)
 * only work once we actually have posts — when the campaign has zero posts
 * we used to fall back to "show every column", which produced a 13-column
 * table that overflowed the card. That made headers truncate ("Total int…")
 * and pushed the empty-state copy off-center because the empty fallback is
 * rendered inside the full-width tbody.
 *
 * Instead, when empty, we show a focused set of columns that previews what
 * the table will look like once posts arrive. The selection per stream:
 *   - all:   high-level cross-stream view
 *   - feed:  engagement signals that matter on feed posts
 *   - story: impressions-first, no eng-rate (stories don't expose it)
 *   - reels: video views + eng-rate
 *
 * Columns hidden by `DATE_FILTERED_HIDDEN_COLUMNS` in OrganicPostsTable are
 * removed downstream, so it's fine to list e.g. `followers` here.
 */
const EMPTY_STATE_DEFAULT_COLUMNS: Record<SocialStreamTab, Set<ColumnId>> = {
  all: new Set([
    COLUMN_IDS.post,
    COLUMN_IDS.creator,
    COLUMN_IDS.followers,
    COLUMN_IDS.engagementRate,
  ]),
  feed: new Set([
    COLUMN_IDS.post,
    COLUMN_IDS.creator,
    COLUMN_IDS.followers,
    COLUMN_IDS.likes,
    COLUMN_IDS.comments,
    COLUMN_IDS.engagementRate,
  ]),
  story: new Set([
    COLUMN_IDS.post,
    COLUMN_IDS.creator,
    COLUMN_IDS.followers,
    COLUMN_IDS.impressions,
  ]),
  reels: new Set([
    COLUMN_IDS.post,
    COLUMN_IDS.creator,
    COLUMN_IDS.followers,
    COLUMN_IDS.views,
    COLUMN_IDS.engagementRate,
  ]),
};

const columnVisibilityEvaluators: Record<ColumnId, ColumnVisibilityEvaluator> =
  {
    [COLUMN_IDS.post]: () => true, // Always shown - essential metadata
    [COLUMN_IDS.creator]: () => true, // Always shown - essential metadata
    [COLUMN_IDS.followers]: (posts) =>
      posts.some((post) => (post.followerCount ?? 0) > 0),
    [COLUMN_IDS.impressions]: (posts) =>
      posts.some((post) => (post.impressionCount ?? 0) > 0),
    [COLUMN_IDS.reach]: (posts) =>
      posts.some((post) => (post.reachCount ?? 0) > 0),
    [COLUMN_IDS.comments]: (posts) =>
      posts.some((post) => (post.commentCount ?? 0) > 0),
    [COLUMN_IDS.likes]: (posts) =>
      posts.some((post) => (post.likeCount ?? 0) > 0),
    [COLUMN_IDS.saves]: (posts) =>
      posts.some((post) => (post.savedCount ?? 0) > 0),
    [COLUMN_IDS.views]: (posts) =>
      posts.some((post) => (post.viewCount ?? 0) > 0),
    [COLUMN_IDS.avgWatchTime]: (posts) =>
      posts.some((post) => (post.videoAverageWatchTime ?? 0) > 0),
    [COLUMN_IDS.videoViewsOrganic]: (posts) =>
      posts.some((post) => (post.videoViewsOrganic ?? 0) > 0),
    [COLUMN_IDS.videoViewsPaid]: (posts) =>
      posts.some((post) => (post.videoViewsPaid ?? 0) > 0),
    [COLUMN_IDS.favoritesOrganic]: (posts) =>
      posts.some((post) => (post.favoritesOrganic ?? 0) > 0),
    [COLUMN_IDS.favoritesPaid]: (posts) =>
      posts.some((post) => (post.favoritesPaid ?? 0) > 0),
    [COLUMN_IDS.shares]: (posts) =>
      posts.some((post) => (post.shareCount ?? 0) > 0),
    [COLUMN_IDS.totalInteractions]: (posts) =>
      posts.some((post) => (post.totalInteractionsCount ?? 0) > 0),
    [COLUMN_IDS.engagementRate]: () => true,
    [COLUMN_IDS.emv]: () => true,
  };

// Helper function to compute default column visibility based on actual post data
// Check if any post has a non-zero value for each metric
const computeDefaultVisibility = (
  postsArray: SocialMediaPost[],
  network?: string | null,
  socialStream?: SocialStreamTab,
  showEmv = false
): Record<ColumnId, boolean> => {
  const result = {} as Record<ColumnId, boolean>;
  const engagementAccessor = getEngagementAccessorByNetwork(network);

  // No posts → show a focused per-stream column set instead of every column.
  // Showing all 13 columns made the table overflow the card and pushed the
  // empty-state copy off-center (see EMPTY_STATE_DEFAULT_COLUMNS above).
  if (postsArray.length === 0) {
    const emptyDefaultsSet = EMPTY_STATE_DEFAULT_COLUMNS[socialStream ?? 'all'];
    Object.values(COLUMN_IDS).forEach((columnId) => {
      // Empty-state sets are keyed only by stream, so apply availability too
      // (otherwise the `story` set would surface Impressions for TikTok).
      result[columnId] =
        emptyDefaultsSet.has(columnId) &&
        isColumnAvailable(columnId, network, socialStream);
    });
    result[COLUMN_IDS.emv] = showEmv;

    // Show Views in place of the hidden Impressions column so the empty state
    // still previews a metric.
    if (
      network === TIKTOK_NETWORK &&
      emptyDefaultsSet.has(COLUMN_IDS.impressions)
    ) {
      result[COLUMN_IDS.views] = true;
    }
    return result;
  }

  // Evaluate visibility for each column using its evaluation function
  Object.values(COLUMN_IDS).forEach((columnId) => {
    if (!isColumnAvailable(columnId, network, socialStream)) {
      result[columnId] = false;
      return;
    }

    if (columnId === COLUMN_IDS.emv) {
      result[columnId] = showEmv;
      return;
    }

    result[columnId] =
      columnId === COLUMN_IDS.engagementRate
        ? postsArray.some((post) => (post[engagementAccessor] ?? 0) > 0)
        : columnVisibilityEvaluators[columnId](postsArray);
  });

  return result;
};

/**
 * Custom hook to manage column visibility based on post data availability.
 * Automatically shows/hides columns based on whether posts have data for each metric,
 * while preserving user preferences for column visibility.
 *
 * @param posts - Array of social media posts to analyze for data availability
 * @returns Object containing columnVisibility state and setter function
 */
function useColumnVisibility(
  posts: SocialMediaPost[],
  network?: string | null,
  socialStream?: SocialStreamTab,
  showEmv = false
) {
  // Track explicit user preferences for column visibility
  // Maps column ID to the boolean value the user explicitly set
  // This allows us to preserve user preferences even when filtered data changes
  const userPreferencesRef = useRef<Map<ColumnId, boolean>>(new Map());

  // User-controlled column visibility (can override defaults)
  const [columnVisibility, setColumnVisibilityState] = useState<
    Record<ColumnId, boolean>
  >(() => computeDefaultVisibility(posts, network, socialStream, showEmv));

  // Track previous default visibility values to avoid unnecessary updates
  const prevDefaultVisibilityRef = useRef<Record<ColumnId, boolean>>(
    computeDefaultVisibility(posts, network, socialStream, showEmv)
  );

  // Sync column visibility when posts change (e.g., when posts load)
  // Auto-hide columns with no data, but preserve user preferences
  useEffect(() => {
    const defaultColumnVisibility = computeDefaultVisibility(
      posts,
      network,
      socialStream,
      showEmv
    );

    // Only update if the actual values have changed
    const hasChanged = Object.keys(defaultColumnVisibility).some(
      (key) =>
        prevDefaultVisibilityRef.current[
          key as keyof typeof defaultColumnVisibility
        ] !==
        defaultColumnVisibility[key as keyof typeof defaultColumnVisibility]
    );

    if (!hasChanged) {
      return;
    }

    // Update the ref with new values
    prevDefaultVisibilityRef.current = defaultColumnVisibility;

    setColumnVisibilityState(() => {
      // Merge defaults with user preferences
      const merged = {...defaultColumnVisibility} as Record<ColumnId, boolean>;
      Object.entries(defaultColumnVisibility).forEach(([key, defaultValue]) => {
        const columnId = key as ColumnId;
        // If user has explicitly set a preference, use it
        if (userPreferencesRef.current.has(columnId)) {
          merged[columnId] = userPreferencesRef.current.get(columnId)!;
        } else {
          // No user preference - use default (which hides columns with no data)
          merged[columnId] = defaultValue;
        }
        // Availability wins over a stored preference (unavailable stays hidden).
        if (!isColumnAvailable(columnId, network, socialStream)) {
          merged[columnId] = false;
        }
      });
      return merged;
    });
  }, [network, posts, socialStream, showEmv]);

  // Wrapper for setColumnVisibility that tracks user preferences
  // When user explicitly changes column visibility, store their preferences
  const setColumnVisibility = useCallback(
    (updater: Parameters<typeof setColumnVisibilityState>[0]) => {
      setColumnVisibilityState((prev) => {
        const newState =
          typeof updater === 'function' ? updater(prev) : updater;

        // Compute current defaults to compare against
        const defaultColumnVisibility = computeDefaultVisibility(
          posts,
          network,
          socialStream,
          showEmv
        );

        // When user explicitly changes visibility, compare with current defaults
        // and store any differences as user preferences
        Object.keys(newState).forEach((key) => {
          const columnId = key as ColumnId;

          // Never persist or honor a preference for an unavailable column.
          if (!isColumnAvailable(columnId, network, socialStream)) {
            newState[columnId] = false;
            userPreferencesRef.current.delete(columnId);
            return;
          }

          const newValue = newState[columnId];
          const defaultValue = defaultColumnVisibility[columnId];

          if (newValue !== defaultValue) {
            // User preference differs from default - store it
            userPreferencesRef.current.set(columnId, newValue);
          } else {
            // User set it back to default - remove preference
            userPreferencesRef.current.delete(columnId);
          }
        });

        return newState;
      });
    },
    [network, posts, socialStream, showEmv]
  );

  return {
    columnVisibility,
    setColumnVisibility,
  };
}

export default useColumnVisibility;
